import React, { useEffect, useState } from "react";
import api from "../lib/apiClient";
import { useColors } from "../context/ColorContext";
import { STATUSES } from "../lib/statusConfig";
import { StatusBadge, DisciplineTag } from "./StatusBadge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";

function ColorRow({ category, name, current, hasOverride, onCommit, onReset, preview }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`color-row-${category}-${name}`}>
      <div className="flex items-center gap-3 min-w-0">
        <input
          type="color"
          key={`${name}-${current}`}
          defaultValue={current}
          data-testid={`color-input-${category}-${name}`}
          onBlur={(e) => onCommit(category, name, e.target.value)}
          className="w-8 h-8 rounded-sm border border-border cursor-pointer bg-card p-0.5"
          title={`Pick a color for ${name}`}
        />
        <span className="text-sm truncate">{name}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {preview}
        <Button
          variant="ghost" size="sm"
          className={`h-8 rounded-sm text-muted-foreground ${hasOverride ? "" : "invisible"}`}
          data-testid={`color-reset-${category}-${name}`}
          onClick={() => onReset(category, name)}
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function ColorManager() {
  const { overrides, refresh, defaultStatusColor, defaultDisciplineColor } = useColors();
  const [disciplines, setDisciplines] = useState([]);

  useEffect(() => {
    api.get("/options").then((r) => setDisciplines(r.data.disciplines || [])).catch(() => {});
  }, []);

  const commit = async (category, name, color) => {
    const existing = overrides[category]?.[name];
    if ((existing || "").toLowerCase() === (color || "").toLowerCase()) return;
    try {
      await api.put("/colors", { category, name, color });
      refresh();
      toast.success(`Updated color for “${name}”`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update color");
    }
  };

  const reset = async (category, name) => {
    try {
      await api.put("/colors", { category, name, color: "" });
      refresh();
      toast.success(`Reset “${name}” to default`);
    } catch (e) {
      toast.error("Failed to reset color");
    }
  };

  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden mb-6" data-testid="tag-colors">
      <div className="px-5 py-3.5 border-b border-border">
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Tag Colors</h2>
        <p className="text-xs text-muted-foreground mt-1">Customize the color of Status badges and Discipline tags shown across the app.</p>
      </div>
      <div className="p-5">
        <Tabs defaultValue="statuses">
          <TabsList className="rounded-sm">
            <TabsTrigger value="statuses" data-testid="color-tab-statuses" className="rounded-sm">Statuses</TabsTrigger>
            <TabsTrigger value="disciplines" data-testid="color-tab-disciplines" className="rounded-sm">Disciplines</TabsTrigger>
          </TabsList>

          <TabsContent value="statuses" className="mt-4">
            <div className="border border-border rounded-sm divide-y divide-border">
              {STATUSES.map((name) => (
                <ColorRow
                  key={name}
                  category="statuses"
                  name={name}
                  current={overrides.statuses?.[name] || defaultStatusColor(name)}
                  hasOverride={Boolean(overrides.statuses?.[name])}
                  onCommit={commit}
                  onReset={reset}
                  preview={<StatusBadge status={name} />}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="disciplines" className="mt-4">
            <div className="border border-border rounded-sm divide-y divide-border">
              {disciplines.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">No disciplines yet.</div>
              )}
              {disciplines.map((name) => (
                <ColorRow
                  key={name}
                  category="disciplines"
                  name={name}
                  current={overrides.disciplines?.[name] || defaultDisciplineColor(name)}
                  hasOverride={Boolean(overrides.disciplines?.[name])}
                  onCommit={commit}
                  onReset={reset}
                  preview={<DisciplineTag discipline={name} />}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
